import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { Moment } from 'moment';
import { CustomerServiceApi } from '../apis/customer-service-api';
import { SSOConfig } from '../models/sso-config-interface';
import CloseableErrorMessage from '../components/CloseableErrorMessage';
import { CSUserProfile } from '../models/csuser-profile-interface';
import { Feature } from '../models/product-interface';
import { ProductSupport } from '../models/product-rate-plan-interface';
import { UserTypeUpdate } from '../models/user-profile-type-interface';
import { CompanySupportTier } from '../models/company-support-tier-interface';
import { DeletionApprovalStatusType } from '../constants/deletionApprovalStatus';

export const CustomerServiceQueries = {
  useSalesRepDropdown: () =>
    useQuery({
      queryKey: ['salesRepDropdown'],
      queryFn: async () => await CustomerServiceApi.getSalesRepDropDown(),
      onError: () => CloseableErrorMessage('Error getting sales rep information.'),
    }),
  useUserList: () =>
    useQuery({
      queryKey: ['userListDropdown'],
      queryFn: async () => await CustomerServiceApi.getUserList(),
      onError: () => CloseableErrorMessage('Error getting CS Portal Users.'),
    }),
  useUserDetail: (userId: number) =>
    useQuery({
      queryKey: ['userDetail', userId],
      queryFn: async () => await CustomerServiceApi.getUserDetail(userId),
      enabled: !!userId,
      onError: () => CloseableErrorMessage('Error getting CS Portal Users.'),
    }),
  useUserDashboards: (userId: number, companyId: number) =>
    useQuery({
      queryKey: ['userDashboards', userId, companyId],
      queryFn: async () => await CustomerServiceApi.getUserDashboards(userId, companyId),
      enabled: !!userId,
      onError: () => CloseableErrorMessage(`Error getting Dashboards for user on company ${companyId}.`),
    }),
  useSearch: (text: string | null) =>
    useQuery({
      queryKey: ['search', text],
      queryFn: async () => await CustomerServiceApi.getSearch(text!),
      enabled: !!text?.trim()?.length,
      onError: () => CloseableErrorMessage('Error getting search results.'),
    }),
  usePutUser: (userProfile: CSUserProfile) =>
    useMutation({
      mutationFn: async (userProfile: CSUserProfile) => await CustomerServiceApi.putUserProfile(userProfile),
      onError: () => CloseableErrorMessage('Error getting search results.'),
    }),
  usePostUser: (userProfile: CSUserProfile) =>
    useMutation({
      mutationFn: async (userProfile: CSUserProfile) => await CustomerServiceApi.postUserProfile(userProfile),
      onError: () => CloseableErrorMessage('Error getting search results.'),
    }),
  usePutUserType: (userId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (userUpdate: UserTypeUpdate) => await CustomerServiceApi.putUserType(userUpdate),
      onError: () => CloseableErrorMessage('Error updating user.'),
      onSuccess: (data) => {
        queryClient.invalidateQueries({queryKey: ['userCompanyProfiles', userId]});
      }
    })
  },
  useToggleDemo: (companyId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (isDemo: boolean) => await CustomerServiceApi.putCompanyDemo(companyId, isDemo),
      onError: () => CloseableErrorMessage('Error updating demo company flag.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['companyDetails', companyId]});
        message.success('Updated demo company flag successfully.');
      }
    })
  },
  useErrorLogList: () =>
    useQuery({
      queryKey: ['errorLogList'],
      queryFn: async () => await CustomerServiceApi.getErrorLogs(),
      onError: () => CloseableErrorMessage('Error getting error log.'),
    }),
  useSalesforceLogList: () =>
    useQuery({
      queryKey: ['salesforceErrorLogList'],
      queryFn: async () => await CustomerServiceApi.getSalesforceLogs(),
      onError: () => CloseableErrorMessage('Error getting salesforce error log.'),
    }),
  useErrorLogsSinceDateList: (date: Moment) =>
    useQuery({
      queryKey: ['errorLogsSinceDate', date.toISOString()],
      queryFn: async () => await CustomerServiceApi.getErrorLogsSinceDate(date.toISOString()),
      onError: () => CloseableErrorMessage('Error getting all api logs.'),
      staleTime: 60000,
      enabled: !!date
    }),
  useAccessLogsBetweenDateList: (startDate: Moment, endDate: Moment) =>
    useQuery({
      queryKey: ['errorLogsSinceDate', startDate.toISOString(), endDate.toISOString()],
      queryFn: async () => await CustomerServiceApi.getAccessLogs(startDate.toISOString(), endDate.toISOString()),
      onError: () => CloseableErrorMessage('Error getting access logs.'),
      staleTime: 60000,
      enabled: !!startDate && !!endDate
    }),
  useUserTypeLogsBetweenDateList: (startDate: Moment, endDate: Moment) =>
    useQuery({
      queryKey: ['userTypeLogsSinceDate', startDate.toISOString(), endDate.toISOString()],
      queryFn: async () => await CustomerServiceApi.getUserTypeLogs(startDate.toISOString(), endDate.toISOString()),
      onError: () => CloseableErrorMessage('Error getting user type logs.'),
      staleTime: 60000,
      enabled: !!startDate && !!endDate
    }),
  useMeetingListForDate: (date: Moment, timezoneId: number) => 
    useQuery({
      queryKey: ['errorLogsSinceDate', date.toISOString(), timezoneId],
      queryFn: async () => await CustomerServiceApi.getMeetingsForDate(date.toISOString(), timezoneId),
      onError: () => CloseableErrorMessage('Error getting meetings.'),
      staleTime: 600000,
      enabled: !!date && !!timezoneId
    }),
  useDemoFeaturesList: () => 
  useQuery({
    queryKey: ['demoFeatures'],
    queryFn: async () => await CustomerServiceApi.getDemoFeatures(),
    onError: () => CloseableErrorMessage('Error getting demo features.'),
    staleTime: 600000,
  }),
  useUpdateDemoFeatures: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: Feature[]) => await CustomerServiceApi.updateDemoFeatures(data),
      onError: () => CloseableErrorMessage('Error updating demo features.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['demoFeatures']});
      }
    })
  },
  useDeleteDemoFeatures: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: Feature[]) => await CustomerServiceApi.deleteDemoFeatures(data),
      onError: () => CloseableErrorMessage('Error removing demo features.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['demoFeatures']});
      }
    })
  },
  useImportUsers: (companyId: number, markImportAsActive: boolean) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: async (data: FormData) => await CustomerServiceApi.postImportUsers(companyId, data, null, markImportAsActive),
        onError: () => CloseableErrorMessage('Error importing users.'),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ['companyUsersSimple', companyId]});
        }
      })
    },
  useUpdateProductSupport: (productId: number | null) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: async (data: ProductSupport) => await CustomerServiceApi.putSupportTier(data),
        onError: () => CloseableErrorMessage('Error updating product support tier.'),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ['supportTiers', productId]});
        }
      })
    },
  useDeleteSupportFromProduct: (productId: number) => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: async (data: ProductSupport) => await CustomerServiceApi.deleteSupportTierFromProduct(data),
        onError: () => CloseableErrorMessage('Error updating product support tier.'),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ['supportTiers', productId]});
        }
      })
    },
  useSupportTiersForProduct: (productId: number) =>
    useQuery({
      queryKey: ['supportTiers', productId],
      queryFn: async () => await CustomerServiceApi.getSupportTiersForProduct(productId),
      onError: () => CloseableErrorMessage('Error getting support tiers.'),
      staleTime: 600000,
      enabled: !!productId
    }),
  useSupportTiers: () =>
    useQuery({
      queryKey: ['supportTiers'],
      queryFn: async () => await CustomerServiceApi.getSupportTiers(),
      onError: () => CloseableErrorMessage('Error getting support tiers.'),
      staleTime: 600000,
    }),
  useGabiiReport: () =>
    useQuery({
      queryKey: ['gabiiReport'],
      queryFn: async () => await CustomerServiceApi.getGabiiReport(),
      onError: () => CloseableErrorMessage('Error getting gabii information.'),
      staleTime: 600000,
    }),
  useGabiiFeedback: () =>
    useQuery({
      queryKey: ['gabiiFeedback'],
      queryFn: async () => await CustomerServiceApi.getGabiiFeedback(),
      onError: () => CloseableErrorMessage('Error getting gabii feedback information.'),
      staleTime: 600000,
    }),
  useVMList: () =>
    useQuery({
      queryKey: ['vmList'],
      queryFn: async () => await CustomerServiceApi.getVMList(),
      onError: () => CloseableErrorMessage('Error getting VM list information.'),
      staleTime: 60000,
    }),
  useVMDetail: (vmId: number) =>
    useQuery({
      queryKey: ['vmDetail', vmId],
      queryFn: async () => await CustomerServiceApi.getVMDetail(vmId),
      onError: () => CloseableErrorMessage('Error getting Detailed VM information.'),
      staleTime: 60000,
    }),
  useUpdateEnableVM: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putVMEnable(vmId, enable),
      onError: ({context}) => CloseableErrorMessage(`Error ${context.enable ? "enabling" : "disabling"} the VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmDetail', vmId]});
      }
    })
  },
  useUpdateVMAlwaysOn: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putVMAlwaysOn(vmId, enable),
      onError: () => CloseableErrorMessage(`Error toggling Always On for the VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmDetail', vmId]});
      }
    })
  },
  useUpdateVMState: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putVMState(vmId, enable),
      onError: ({context}) => CloseableErrorMessage(`Error turning ${context.enable ? "on" : "off"} the VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmDetail', vmId]});
      }
    })
  },
  useTranscriptVMList: () =>
    useQuery({
      queryKey: ['vmTranscriptList'],
      queryFn: async () => await CustomerServiceApi.getTranscriptVMList(),
      onError: () => CloseableErrorMessage('Error getting transcript VM list information.'),
      staleTime: 60000,
    }),
  useTranscriptVMDetail: (vmId: number) =>
    useQuery({
      queryKey: ['vmTranscriptDetail', vmId],
      queryFn: async () => await CustomerServiceApi.getTranscriptVMDetail(vmId),
      onError: () => CloseableErrorMessage('Error getting detailed transcript VM information.'),
      staleTime: 60000,
    }),
  useUpdateEnableTranscriptVM: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putTranscriptVMEnable(vmId, enable),
      onError: ({context}) => CloseableErrorMessage(`Error ${context.enable ? "enabling" : "disabling"} the transcript VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmTranscriptDetail', vmId]});
      }
    })
  },
  useUpdateTranscriptVMAlwaysOn: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putTranscriptVMAlwaysOn(vmId, enable),
      onError: () => CloseableErrorMessage(`Error toggling Always On for the transcript VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmTranscriptDetail', vmId]});
      }
    })
  },
  useUpdateTranscriptVMState: (vmId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (enable: boolean) => await CustomerServiceApi.putTranscriptVMState(vmId, enable),
      onError: ({context}) => CloseableErrorMessage(`Error turning ${context.enable ? "on" : "off"} the transcript VM.`),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['vmTranscriptDetail', vmId]});
      }
    })
  },
  

  useCompanyUsageMeetings: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageMeetings', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageMeetings(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company meetings usage.'),
}),
useCompanyUsageUsers: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageActivity', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageUsers(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company user usage.'),
}),
useCompanyUsageAdmin: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageAdmin', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageAdmin(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company admin usage.'),
}),
useCompanyUsageMember: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageMember', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageMember(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company member usage.'),
}),
useCompanyUsageTopics: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageTopics', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageTopics(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company topics usage.'),
}),
useCompanyUsageDashboards: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageDashboards', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageDashboards(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company dashboards usage.'),
}),
useCompanyUsageCommitteeSuite: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageCommitteeSuite', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageCommitteeSuite(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company committee suite usage.'),
}),
useCompanyUsageESign: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageESign', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageESign(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company esign usage.'),
}),
useCompanyUsageVotes: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageVotes', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageVotes(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company votes usage.'),
}),
useCompanyUsageSurveys: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageSurveys', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageSurveys(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company surveys usage.'),
}),
useCompanyUsageIntegrations: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageIntegrations', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageIntegrations(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company integrations usage.'),
}),
useCompanyUsageMS365: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageMS365', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageMS365(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company MS 365 usage.'),
}),
useCompanyUsageFileCopy: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageFileCopy', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageFileCopy(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company file copy usage.'),
}),
useCompanyUsageMinutes: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageMinutes', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageMinutes(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company minutes usage.'),
}),
useCompanyUsageGabii: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageGabii', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageGabii(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company gabii usage.'),
}),
useCompanyUsageSearch: (companyId: number | undefined, range: number = 0) =>
  useQuery({
  queryKey: ['companyUsageSearch', companyId, range],
  queryFn: async () => await CustomerServiceApi.getCompanyUsageSearch(companyId!, range),
  enabled: !!companyId,
  onError: () => CloseableErrorMessage('Error getting company search usage.'),
}),
useCompanyAccessLogs: (numOfDays: number = 0) =>
  useQuery({
  queryKey: ['companyAccessLogs', numOfDays],
  queryFn: async () => await CustomerServiceApi.getCompanyEnabledLogs(numOfDays),
  onError: () => CloseableErrorMessage('Error getting company enabled logs.'),
}),
useCompanyProductLogs: (numOfDays: number = 0) =>
  useQuery({
  queryKey: ['companyProductLogs', numOfDays],
  queryFn: async () => await CustomerServiceApi.getCompanyProductLogs(numOfDays),
  onError: () => CloseableErrorMessage('Error getting company product logs.'),
}),
useCompanyFeatureLogs: (numOfDays: number = 0) =>
  useQuery({
  queryKey: ['companyFeatureLogs', numOfDays],
  queryFn: async () => await CustomerServiceApi.getCompanyFeatureLogs(numOfDays),
  onError: () => CloseableErrorMessage('Error getting company feature logs.'),
}),
useSalesforceWebhookLogs: (numOfDays: number = 0) =>
  useQuery({
  queryKey: ['salesforceWebhookLogs', numOfDays],
  queryFn: async () => await CustomerServiceApi.getSalesforceWebhookLogs(numOfDays),
  onError: () => CloseableErrorMessage('Error getting salesforce webhook logs.'),
}),

useUpdateCompanySupportTier: (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CompanySupportTier) => await CustomerServiceApi.postCompanySupportTier(data),
    onError: () => CloseableErrorMessage('Error updating support tier for company.'),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['supportDetails', companyId]});
    }
  })
},
useGetActiveCompanyIds: () =>
  useQuery({
  queryKey: ['getActiveCompanies'],
  queryFn: async () => await CustomerServiceApi.getActiveCompanyIds(),
  onError: () => CloseableErrorMessage('Error getting list of active companies.'),
  staleTime: 1000 * 60 * 60
}),
useGetValidSearchEntities: () =>
  useQuery({
  queryKey: ['getValidSearchEntities'],
  queryFn: async () => await CustomerServiceApi.getValidSearchEntities(),
  onError: () => CloseableErrorMessage('Error getting a list of valid search entity information.'),
  staleTime: 1000 * 60 * 60 * 24
}),
useGetValidSearchSectionTypes: () =>
  useQuery({
  queryKey: ['getValidSearchSectionTypes'],
  queryFn: async () => await CustomerServiceApi.getValidSearchSectionTypes(),
  onError: () => CloseableErrorMessage('Error getting a list of valid section types.'),
  staleTime: 1000 * 60 * 60 * 24
}),
useGetDefaultUserCompanyList: () =>
  useQuery({
  queryKey: ['getDefaultUserCompanyList'],
  queryFn: async () => await CustomerServiceApi.getDefaultUserCompanyList(),
  onError: () => CloseableErrorMessage('Error getting a list of companies the default user is in.'),
  staleTime: 1000 * 60 * 60
}),
useDeleteDefaultUserFromCompanies: () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: number[]) => await CustomerServiceApi.deleteDefaultUsersFromCompanies(data),
    onError: () => CloseableErrorMessage('Error removing the default user.'),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['getDefaultUserCompanyList']});
    }
  })
},
useGetDeleteRequests: () =>
  useQuery({
  queryKey: ['getDeleteRequests'],
  queryFn: async () => await CustomerServiceApi.getDeleteRequests(),
  onError: () => CloseableErrorMessage('Error getting list of delete requests.'),
  staleTime: 1000 * 60 * 1,
  refetchInterval: (data) => data?.some((request) => request.ApprovalTypeId === DeletionApprovalStatusType.InProgress) ? 1000 * 60 * 1 : false
}),
useGetCompaniesThatCanBeDeleted: () =>
  useQuery({
  queryKey: ['getCompaniesThatCanBeDeleted'],
  queryFn: async () => await CustomerServiceApi.getCompaniesThatCanBeDeleted(),
  onError: () => CloseableErrorMessage('Error getting list of companies that can be deleted.'),
  staleTime: 1000 * 60 * 5
}),
useDeleteCompany: () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (companyIds: number[]) => await CustomerServiceApi.postDeleteRequest(companyIds),
    onError: () => CloseableErrorMessage('Error creating delete request.'),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['getDeleteRequests']});
    }
  })
},
useUpdateDeleteRequest: () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { companyIds: number[], deleteStatusId: number }) => await CustomerServiceApi.putDeleteRequest(data.companyIds, data.deleteStatusId),
    onError: () => CloseableErrorMessage('Error updating delete request.'),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['getDeleteRequests']});
    }
  })
},

// SSO Configuration Queries
useGetXmlFromUrl: (url: string, enabled: boolean = false) =>
  useQuery({
    queryKey: ['getXmlFromUrl', url],
    queryFn: async () => await CustomerServiceApi.getXmlFromUrl(url),
    enabled: enabled && !!url,
    onError: () => CloseableErrorMessage('Error fetching XML from URL.'),
    staleTime: 0,
    cacheTime: 0
  }),
useGetSSOConfig: (companyId: number, applicationId: number, enabled: boolean = false) =>
  useQuery({
    queryKey: ['getSSOConfig', companyId, applicationId],
    queryFn: async () => await CustomerServiceApi.getSSOConfig(companyId, applicationId),
    enabled: enabled && !!companyId && !!applicationId,
    onError: () => CloseableErrorMessage('Error fetching SSO configuration.'),
    staleTime: 1000 * 60 * 5
  }),
usePostSsoConfig: () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, data }: { companyId: number; data: SSOConfig }) => 
      await CustomerServiceApi.postSsoConfig(companyId, data),
    onError: () => CloseableErrorMessage('Failed to add SSO configuration. Please try again.'),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({queryKey: ['getSSOConfig', variables.companyId]});
      queryClient.invalidateQueries({queryKey: ['companyDetails', variables.companyId]});
    }
  });
},
usePutSsoConfig: () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, data }: { companyId: number; data: SSOConfig }) => 
      await CustomerServiceApi.putSsoConfig(companyId, data),
    onError: () => CloseableErrorMessage('Failed to update SSO configuration. Please try again.'),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({queryKey: ['getSSOConfig', variables.companyId]});
      queryClient.invalidateQueries({queryKey: ['companyDetails', variables.companyId]});
    }
  });
},
};
